import { addDays, differenceInDays, isAfter, isBefore, isValid, parseISO } from "date-fns";

// Helper functions for plan management

/**
 * Checks if the company is on the Start (free) plan
 * Start plan includes: null, undefined, empty string, 'gratuito', 'start'
 */
export const isStartPlan = (plan: string | null | undefined): boolean => {
  return !plan || plan === 'gratuito' || plan === 'start';
};

/**
 * Calculates remaining days until expiration
 */
export const getDaysRemaining = (expiresAt: string): number => {
  if (!expiresAt) return 0;
  const expiresDate = parseISO(expiresAt);
  if (!isValid(expiresDate)) return 0;
  
  const now = new Date();
  const days = differenceInDays(expiresDate, now);
  return Math.max(0, days);
};

/**
 * Returns a user-friendly display name for the plan
 */
export const getPlanDisplayName = (plan: string | null): string => {
  if (!plan || plan === 'gratuito' || plan === 'start') {
    return 'Start';
  }
  
  if (plan.startsWith('profissional_')) {
    const vagas = plan.replace('profissional_', '');
    return `Profissional (${vagas} vagas/mês)`;
  }
  
  // Fallback for other plans
  return plan.charAt(0).toUpperCase() + plan.slice(1);
};

/**
 * Checks if the company has active (non-expired) credits
 */
export const hasActiveCredits = (
  credits: number | null | undefined,
  creditsExpiresAt: string | null | undefined
): boolean => {
  if (!credits || credits <= 0) return false;
  if (!creditsExpiresAt) return false;
  
  const expiresDate = parseISO(creditsExpiresAt);
  if (!isValid(expiresDate)) return false;
  
  return isAfter(expiresDate, new Date());
};

/**
 * Checks if the company has premium access (paid plan OR active credits)
 */
export const hasPremiumAccess = (
  plan: string | null | undefined,
  credits: number | null | undefined,
  creditsExpiresAt: string | null | undefined
): boolean => {
  // Professional plan always has access
  if (plan?.startsWith('profissional_')) return true;
  
  // Has active credits
  return hasActiveCredits(credits, creditsExpiresAt);
};

/**
 * Checks if the professional subscription is currently paid
 * Returns true if subscription_paid_until is in the future
 */
export const isSubscriptionPaid = (
  subscriptionPaidUntil: string | null | undefined
): boolean => {
  if (!subscriptionPaidUntil) return false;
  const paidUntil = parseISO(subscriptionPaidUntil);
  if (!isValid(paidUntil)) return false;
  
  return isAfter(paidUntil, new Date());
};

/**
 * Checks if subscription is in grace period (0-5 days overdue)
 * Jobs should be paused during this period
 */
export const isInGracePeriod = (
  subscriptionPaidUntil: string | null | undefined
): boolean => {
  if (!subscriptionPaidUntil) return false;
  
  const paidUntil = parseISO(subscriptionPaidUntil);
  if (!isValid(paidUntil)) return false;
  
  const now = new Date();
  
  // If still paid, not in grace period
  if (isAfter(paidUntil, now)) return false;
  
  // Check if within 5 days of expiration
  const gracePeriodEnd = addDays(paidUntil, 5);
  
  return isBefore(now, gracePeriodEnd) || now.getTime() === gracePeriodEnd.getTime();
};

/**
 * Checks if the company can create a new job based on their plan limits
 */
export const canCreateJob = (
  plan: string | null | undefined,
  activeJobsCount: number,
  credits: number | null | undefined = 0
): boolean => {
  // Start plan: limit 1 active job
  if (isStartPlan(plan)) {
    return activeJobsCount < 1;
  }
  
  // Professional plan: check limit based on plan name (e.g., profissional_10)
  if (plan?.startsWith('profissional_')) {
    const limitStr = plan.replace('profissional_', '');
    const limit = parseInt(limitStr, 10);
    
    // If parse fails or limit is 0 (unlikely), default to blocking or generous?
    if (!isNaN(limit)) {
      return activeJobsCount < limit;
    }
    return true; 
  }
  
  // Credits logic: if they have job credits, they can create
  if (credits && credits > 0) return true;
  
  return false;
};

/**
 * Checks if the company can use featured job option
 */
export const canUseFeatured = (
  plan: string | null | undefined,
  credits: number | null | undefined = 0
): boolean => {
  // Professional plans usually include featured jobs or better visibility
  if (plan?.startsWith('profissional_')) return true;
  
  // If they have credits specifically for featured? 
  if (credits && credits > 0) return true;
  
  return false;
};
